import { Box, Fade, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Container, Section, Text } from '../components/atomic';
import SEO from '../components/SEO';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Terms of Use page component for EllaFin
 *
 * Dark navy aesthetic matching the Homepage/About/Product pages:
 * - Dark navy hero section with white text
 * - Dark content section with readable light typography
 * - Accent green for list markers and section headings
 * - Clean, readable typography optimized for legal content
 * - Print-friendly layout
 * - Proper heading hierarchy (h2 for main sections)
 *
 * @returns {JSX.Element} Terms of Use page component
 */
function TermsOfUse() {
  const [isLoaded, setIsLoaded] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  /**
   * Common styles for legal document sections
   */
  const sectionStyles = {
    mb: 5,
  };

  const sectionHeadingStyles = {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    color: '#ffffff !important',
    mb: 2,
    textAlign: 'left',
    fontFamily: '"Inter", "Helvetica", sans-serif',
  };

  const paragraphStyles = {
    lineHeight: 1.8,
    color: alpha('#ffffff', 0.8),
    mb: 2,
    fontSize: { xs: '1rem', md: '1.1rem' },
  };

  const listStyles = {
    pl: { xs: 3, md: 4 },
    m: 0,
    mb: 2,
  };

  const listItemStyles = {
    mb: 1.5,
    color: alpha('#ffffff', 0.8),
    lineHeight: 1.7,
    fontSize: { xs: '1rem', md: '1.1rem' },
    '&::marker': { color: theme.palette.secondary.main },
  };

  return (
    <LegalPageLayout>
      <SEO
        title="Terms of Use"
        description="EllaFin Terms of Use governing access to the website and services. Covers use license, user accounts, prohibited uses, intellectual property, and limitation of liability."
        path="/terms"
        noIndex={false}
        keywords="terms of use, terms of service, user agreement, legal terms"
      />

      {/* Hero Section */}
      <Box
        component="section"
        aria-label="Terms of Use hero"
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
          backgroundColor: '#1e2140',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Fade in={isLoaded} timeout={600}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: theme.palette.secondary.main,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    fontSize: '0.875rem',
                    mb: 2,
                    display: 'block',
                  }}
                >
                  Legal
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={800}>
              <Box>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Terms of Use
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={1000}>
              <Box>
                <Text
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    color: alpha('#ffffff', 0.7),
                    lineHeight: 1.7,
                    maxWidth: '650px',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  Welcome to EllaFin. These Terms of Use govern your use of our
                  website, AWS Marketplace consulting services, and any related
                  content.
                </Text>
                <Text
                  sx={{
                    fontSize: '0.9rem',
                    color: alpha('#ffffff', 0.5),
                    fontStyle: 'italic',
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  Last Updated: December 2025
                </Text>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Legal Document Content */}
      <Section
        sx={{
          backgroundColor: '#13152b',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Box
            component="article"
            sx={{
              px: { xs: 2, md: 0 },
            }}
          >
            {/* Section 1 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                1. Acceptance of Terms
              </Typography>
              <Text sx={paragraphStyles}>
                By accessing and using this website, you accept and agree to be
                bound by the terms and provision of this agreement. If you do
                not agree to abide by the above, please do not use this service.
              </Text>
            </Box>

            {/* Section 2 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                2. Use License
              </Typography>
              <Text sx={paragraphStyles}>
                Permission is granted to temporarily download one copy of the
                materials on EllaFin's website for personal, non-commercial
                transitory viewing only. This is the grant of a license, not a
                transfer of title, and under this license you may not:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  Modify or copy the materials;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Use the materials for any commercial purpose or for any public
                  display;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Attempt to reverse engineer any software contained on the
                  website;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Remove any copyright or other proprietary notations from the
                  materials.
                </Box>
              </Box>
              <Text sx={paragraphStyles}>
                This license shall automatically terminate if you violate any of
                these restrictions and may be terminated by EllaFin at any time.
              </Text>
            </Box>

            {/* Section 3 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                3. User Accounts
              </Typography>
              <Text sx={paragraphStyles}>
                When you create an account with us, you must provide information
                that is accurate, complete, and current at all times. You are
                responsible for safeguarding the password and for maintaining
                the confidentiality of your account.
              </Text>
              <Text sx={paragraphStyles}>
                You agree not to disclose your password to any third party and
                to take sole responsibility for any activities or actions under
                your account, whether or not you have authorized such
                activities.
              </Text>
            </Box>

            {/* Section 4 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                4. Prohibited Uses
              </Typography>
              <Text sx={paragraphStyles}>You may not use our service:</Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  For any unlawful purpose or to solicit others to perform
                  unlawful acts;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To violate any international, federal, provincial, or state
                  regulations, rules, laws, or local ordinances;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To infringe upon or violate our intellectual property rights
                  or the intellectual property rights of others;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To harass, abuse, insult, harm, defame, slander, disparage,
                  intimidate, or discriminate;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To submit false or misleading information;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To upload or transmit viruses or any other type of malicious
                  code;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To spam, phish, pharm, pretext, spider, crawl, or scrape.
                </Box>
              </Box>
            </Box>

            {/* Section 5 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                5. Content
              </Typography>
              <Text sx={paragraphStyles}>
                Our service allows you to post, link, store, share and otherwise
                make available certain information, text, graphics, videos, or
                other material. You are responsible for the content that you
                post to the service, including its legality, reliability, and
                appropriateness.
              </Text>
              <Text sx={paragraphStyles}>
                By posting content to the service, you grant us the right and
                license to use, modify, publicly perform, publicly display,
                reproduce, and distribute such content on and through the
                service.
              </Text>
            </Box>

            {/* Section 6 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                6. Intellectual Property Rights
              </Typography>
              <Text sx={paragraphStyles}>
                The service and its original content, features, and
                functionality are and will remain the exclusive property of
                EllaFin and its licensors. The service is protected by
                copyright, trademark, and other laws of both the United States
                and foreign countries. Our trademarks and trade dress may not be
                used in connection with any product or service without the prior
                written consent of EllaFin.
              </Text>
            </Box>

            {/* Section 7 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                7. Termination
              </Typography>
              <Text sx={paragraphStyles}>
                We may terminate or suspend your account and bar access to the
                service immediately, without prior notice or liability, under
                our sole discretion, for any reason whatsoever and without
                limitation, including but not limited to a breach of the Terms.
              </Text>
              <Text sx={paragraphStyles}>
                If you wish to terminate your account, you may simply
                discontinue using the service. All provisions of the Terms which
                by their nature should survive termination shall survive
                termination, including, without limitation, ownership
                provisions, warranty disclaimers, indemnity, and limitations of
                liability.
              </Text>
            </Box>

            {/* Section 8 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                8. Disclaimer and Limitation of Liability
              </Typography>
              <Text sx={paragraphStyles}>
                The information on this website is provided on an "as is" basis.
                To the fullest extent permitted by law, this Company excludes
                all representations, warranties, conditions and terms (express
                or implied, statutory or otherwise) other than those expressly
                set out in the Terms and Conditions.
              </Text>
              <Text sx={paragraphStyles}>
                In no event shall EllaFin, nor its directors, employees,
                partners, agents, suppliers, or affiliates, be liable for any
                indirect, incidental, special, consequential, or punitive
                damages, including without limitation, loss of profits, data,
                use, goodwill, or other intangible losses, resulting from:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  Your access to or use of or inability to access or use the
                  service;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Any conduct or content of any third party on the service;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Any content obtained from the service;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Unauthorized access, use, or alteration of your transmissions
                  or content.
                </Box>
              </Box>
            </Box>

            {/* Section 9 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                9. Governing Law
              </Typography>
              <Text sx={paragraphStyles}>
                These Terms shall be interpreted and governed by the laws of the
                State of California, without regard to conflict of law
                provisions. Our failure to enforce any right or provision of
                these Terms will not be considered a waiver of those rights.
              </Text>
              <Text sx={paragraphStyles}>
                If any provision of these Terms is held to be invalid or
                unenforceable by a court, the remaining provisions of these
                Terms will remain in effect.
              </Text>
            </Box>

            {/* Section 10 */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                10. Changes to Terms
              </Typography>
              <Text sx={paragraphStyles}>
                We reserve the right, at our sole discretion, to modify or
                replace these Terms at any time. If a revision is material, we
                will provide at least 30 days notice prior to any new terms
                taking effect. What constitutes a material change will be
                determined at our sole discretion.
              </Text>
              <Text sx={paragraphStyles}>
                By continuing to access or use our service after those revisions
                become effective, you agree to be bound by the revised terms.
              </Text>
            </Box>

            {/* Section 11 - Contact Information */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                11. Contact Information
              </Typography>
              <Text sx={paragraphStyles}>
                If you have any questions about these Terms of Use, please
                contact us at:
              </Text>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  backgroundColor: alpha('#ffffff', 0.04),
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  mt: 3,
                }}
              >
                <Text
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#ffffff',
                    fontSize: '1.1rem',
                  }}
                >
                  EllaFin
                </Text>
                <Text sx={{ lineHeight: 1.8, color: alpha('#ffffff', 0.7) }}>
                  401 West A St. Ste. 200
                  <br />
                  San Diego, CA 92101
                  <br />
                  <br />
                  Email:{' '}
                  <SecureEmail
                    emailKey="support"
                    showAsLink={false}
                    component="span"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                </Text>
              </Box>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default TermsOfUse;
